import { Formik, FormikHelpers, FormikProps, FormikValues } from 'formik'
import React, { FC, useCallback } from 'react'
import { FormikHandleSubmit } from '@typings/Formik'
import TransferForm, { ExternalFormProps } from './TransferForm'
import createValidationSchema from './validation'
import getOr from 'lodash/fp/getOr'
import { TransferType } from '@typings/entities/Transfer'

export type Props = ExternalFormProps & {
  initialValues: FormikValues
  onSave: FormikHandleSubmit
  onSaveAndSend: FormikHandleSubmit
  onCancel: FormikHandleSubmit
  onBack: () => void
  isCreatingNew: boolean
  newBookingEnabled?: boolean
}

const initialValuesEmpty = {}

const TransferFormFormik: FC<Props> = ({
  initialValues = initialValuesEmpty,
  onSave,
  isCreatingNew,
  onSaveAndSend,
  onCancel,
  newBookingEnabled,
  ...rest
}) => {
  const finInitialValues = React.useMemo(
    () => ({
      type: TransferType.virtual,
      ...initialValues,
    }),
    [initialValues],
  )

  const handleSubmit = useCallback(
    ({ button, ...values }: FormikValues & { button?: string }, formikHelpers: FormikHelpers<FormikValues>) => {
      switch (button) {
        case 'save':
          onSave(values, formikHelpers)
          break
        case 'saveAndSend':
          onSaveAndSend(values, formikHelpers)
          break
      }
    },
    [onSave, onSaveAndSend],
  )

  const handleCancel = useCallback(
    (formikHelpers: FormikHelpers<FormikValues>) => (): void => {
      formikHelpers.setSubmitting(true)
      onCancel(getOr(null, 'values', formikHelpers), formikHelpers)
    },
    [onCancel],
  )

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={finInitialValues}
      validationSchema={createValidationSchema({ newBookingEnabled })}
    >
      {(formikBag: FormikProps<FormikValues>) => (
        <TransferForm
          {...formikBag}
          {...rest}
          onCancel={handleCancel(formikBag)}
          isCreatingNew={isCreatingNew}
          newBookingEnabled={newBookingEnabled}
        />
      )}
    </Formik>
  )
}

export default TransferFormFormik
